import axios from 'axios';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest, protectedResources, msalConfig, developmentConfig } from '../config/authConfig';

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Create axios instance with base URL from config or environment variable
const baseURL = protectedResources.apiAccounting.endpoint;

const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Debug logging function (disabled for production)
const debugToken = (message: string, data: any = null) => {
  // No-op in production
};

// Add request interceptor to include authentication token
api.interceptors.request.use(
  async (config: any) => {
    debugToken('API request interceptor called', { url: config.url });
    
    // Skip token acquisition for auth/me endpoint if we're trying to refresh
    const isAuthEndpoint = config.url && (config.url.includes('/auth/me') || config.url.includes('/auth/login'));
    const isRefreshing = config.headers['x-retry-auth'] === 'true';
    
    // Skip token on auth endpoints during refresh to prevent loops
    if (isAuthEndpoint && isRefreshing) {
      debugToken('Skipping token acquisition for auth endpoint during refresh');
      return config;
    }
    
    // In development mode with mock authentication enabled
    if (developmentConfig.enabled) {
      debugToken('Using mock authentication with development token', { 
        enabled: developmentConfig.enabled,
        tokenLength: developmentConfig.developmentToken.length 
      });
      // Add the development token
      config.headers['Authorization'] = `Bearer ${developmentConfig.developmentToken}`;
      return config;
    }

    // Handle real Microsoft authentication
    debugToken('Using Microsoft authentication');
    let activeAccount = msalInstance.getActiveAccount();
    
    // If no active account is set but we have accounts, set the first one as active
    if (!activeAccount && msalInstance.getAllAccounts().length > 0) {
      debugToken('No active account set but accounts exist, setting first account as active');
      const accounts = msalInstance.getAllAccounts();
      msalInstance.setActiveAccount(accounts[0]);
      activeAccount = accounts[0];
    }
    
    if (activeAccount) {
      debugToken('Active account found', { 
        username: activeAccount.username,
        tenantId: activeAccount.tenantId,
        environment: activeAccount.environment,
        localAccountId: activeAccount.localAccountId
      });
      
      try {
        debugToken('Attempting to acquire token silently with scopes:', loginRequest.scopes);
        // Try to acquire token silently first, with explicit scopes
        // Make sure we're using the correct audience format: api://{clientId}/access_as_user
        const response = await msalInstance.acquireTokenSilent({
          scopes: loginRequest.scopes,
          account: activeAccount,
          forceRefresh: false // Set to true if you want to force a token refresh
        });
        
        debugToken('Token acquired successfully', {
          accessToken: response.accessToken ? `${response.accessToken.substring(0, 10)}...` : 'none',
          tokenLength: response.accessToken ? response.accessToken.length : 0,
          scopes: response.scopes,
          account: response.account ? response.account.username : 'none',
          expiresOn: response.expiresOn
        });
        
        // Make sure we're setting the token correctly
        if (response.accessToken) {
          config.headers['Authorization'] = `Bearer ${response.accessToken}`;
          debugToken('Authorization header set successfully');
          
          // Verify that the token is properly formatted
          const tokenParts = response.accessToken.split('.');
          if (tokenParts.length !== 3) {
            console.warn('Token does not appear to be in valid JWT format');
          } else {
            // Parse token claims for validation (without logging)
            try {
              const tokenPayload = JSON.parse(atob(tokenParts[1]));
            
              // Verify the token has the required scope
              const tokenScopes = tokenPayload.scp || tokenPayload.scope || '';
              const hasAccessAsUserScope = typeof tokenScopes === 'string' && 
                (tokenScopes.includes('access_as_user') || tokenScopes.split(' ').includes('access_as_user'));
              
              if (!hasAccessAsUserScope) {
                console.warn('Token does not have the required access_as_user scope');
              }
            } catch (e) {
              console.error('Error parsing token payload:', e);
            }
          }
        } else {
          debugToken('No access token received from MSAL');
          // Attempt to acquire token with popup as fallback
          debugToken('Falling back to popup authentication');
          try {
            const popupResponse = await msalInstance.acquireTokenPopup({
              scopes: loginRequest.scopes
            });
            if (popupResponse.accessToken) {
              config.headers['Authorization'] = `Bearer ${popupResponse.accessToken}`;
              debugToken('Authorization header set after popup login');
            }
          } catch (popupError) {
            debugToken('Popup acquisition failed, using redirect as last resort', { error: popupError });
            // Store current path for redirect after login
            sessionStorage.setItem('redirectPath', window.location.pathname);
            // Fall back to redirect if popup fails
            msalInstance.acquireTokenRedirect({
              scopes: loginRequest.scopes
            });
          }
        }
      } catch (error) {
        // If silent token acquisition fails, the user might need to login again
        if (error instanceof InteractionRequiredAuthError) {
          debugToken('Interaction required for authentication', { errorType: 'InteractionRequiredAuthError' });
          // Store the current URL to redirect back after login
          sessionStorage.setItem('redirectPath', window.location.pathname);
          
          try {
            // Use popup for better experience on some browsers
            debugToken('Attempting to acquire token with popup');
            const popupResponse = await msalInstance.acquireTokenPopup(loginRequest);
            
            if (popupResponse.accessToken) {
              config.headers['Authorization'] = `Bearer ${popupResponse.accessToken}`;
              debugToken('Token acquired successfully with popup', {
                tokenLength: popupResponse.accessToken.length,
                scopes: popupResponse.scopes,
                expiresOn: popupResponse.expiresOn
              });
            }
          } catch (popupError) {
            debugToken('Popup login failed, redirecting to login page', { error: popupError });
            // If popup fails, fall back to redirect
            try {
              await msalInstance.acquireTokenRedirect(loginRequest);
            } catch (redirectError) {
              // If redirect also fails, trigger auth failure event
              debugToken('Redirect login also failed, triggering auth failure', { error: redirectError });
              window.dispatchEvent(authFailedEvent);
            }
          }
        } else {
          debugToken('Error acquiring token', { error: error });
          console.error('Error acquiring token:', error);
        }
      }
    } else {
      console.warn('No active account found, user may not be authenticated');
    }
    
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

const authFailedEvent = new Event('auth:failed');

// Add response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Check if error is due to authentication failure
    if (error.response && error.response.status === 401) {
      debugToken('Authentication failed - 401 Unauthorized response', { 
        url: error.config.url,
        method: error.config.method,
        headers: Object.keys(error.config.headers || {}),
        statusText: error.response.statusText,
        data: error.response.data
      });
      
      // Extract error message from backend response if available
      const errorMessage = error.response.data?.message || error.response.data?.error || 
                           'Authentication failed. Please try again.';
                           
      // Store the auth error message in session storage so login page can display it
      sessionStorage.setItem('authErrorMessage', errorMessage);
      
      // Check if we're already retrying to avoid infinite loops
      const isRetry = error.config.headers['x-retry-auth'] === 'true';
      
      if (!isRetry) {
        // Mark this request as a retry attempt
        error.config.headers['x-retry-auth'] = 'true';
        
        // Try to refresh the token
        try {
          // If using mock auth, just return the original error
          if (developmentConfig.enabled) {
            debugToken('Using mock auth, not attempting token refresh');
            return Promise.reject(error);
          }
          
          // Get active account
          const activeAccount = msalInstance.getActiveAccount();
          
          if (activeAccount) {
            debugToken('Attempting to refresh token for account:', activeAccount.username);
            
            // Force refresh the token
            const response = await msalInstance.acquireTokenSilent({
              scopes: loginRequest.scopes,
              account: activeAccount,
              forceRefresh: true
            });
            
            debugToken('Token refreshed successfully', {
              tokenLength: response.accessToken.length,
              scopes: response.scopes,
              expiresOn: response.expiresOn
            });
            
            // Update the Authorization header with the new token
            error.config.headers['Authorization'] = `Bearer ${response.accessToken}`;
            
            // Retry the request with the new token
            return api(error.config);
          } else {
            debugToken('No active account found for token refresh');
            
            // Try to get any available account
            const accounts = msalInstance.getAllAccounts();
            if (accounts.length > 0) {
              debugToken('Found account to use for refresh:', accounts[0].username);
              
              try {
                // Try popup acquisition as fallback
                const popupResponse = await msalInstance.acquireTokenPopup({
                  scopes: loginRequest.scopes,
                  account: accounts[0]
                });
                
                if (popupResponse.accessToken) {
                  debugToken('Token acquired via popup, retrying request');
                  error.config.headers['Authorization'] = `Bearer ${popupResponse.accessToken}`;
                  return api(error.config);
                }
              } catch (popupError) {
                debugToken('Popup token acquisition failed', { error: popupError });
                // Store current path for redirect after login
                sessionStorage.setItem('redirectPath', window.location.pathname);
                // Redirect to login page
                window.location.href = '/login';
                return Promise.reject(error);
              }
            } else {
              debugToken('No accounts found, redirecting to login');
              // Store current path for redirect after login
              sessionStorage.setItem('redirectPath', window.location.pathname);
              // Redirect to login page
              window.location.href = '/login';
              return Promise.reject(error);
            }
          }
        } catch (refreshError) {
          debugToken('Token refresh failed:', refreshError);
          
          // Check if it's an interaction required error
          if (refreshError instanceof InteractionRequiredAuthError) {
            debugToken('Interaction required for authentication');
            // Store current path for redirect after login
            sessionStorage.setItem('redirectPath', window.location.pathname);
            
            try {
              // Try popup as fallback
              const popupResponse = await msalInstance.acquireTokenPopup({
                scopes: loginRequest.scopes
              });
              
              if (popupResponse.accessToken) {
                debugToken('Token acquired via popup after refresh failure');
                error.config.headers['Authorization'] = `Bearer ${popupResponse.accessToken}`;
                return api(error.config);
              }
            } catch (popupError) {
              debugToken('Popup acquisition also failed', { error: popupError });
              // Redirect to login page
              window.location.href = '/login';
            }
          } else {
            // For other errors, dispatch auth failed event
            window.dispatchEvent(authFailedEvent);
            // Redirect to login page
            window.location.href = '/login';
          }
          
          return Promise.reject(error);
        }
      } else {
        debugToken('Request already attempted refresh, not trying again');
        
        // Dispatch auth failed event
        window.dispatchEvent(authFailedEvent);
        return Promise.reject(error);
      }
    }
    
    return Promise.reject(error);
  }
);

// API services
export const authService = {
  getCurrentUser: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout')
};

export const categoryService = {
  getAll: (params?: any) => api.get('/categories', { params }),
  create: (data: any) => api.post('/categories', data),
  update: (id: number, data: any) => api.put(`/categories/${id}`, data),
  delete: (id: number) => api.delete(`/categories/${id}`),
  restore: (id: number) => api.post(`/categories/${id}/restore`),
  permanentDelete: (id: number) => api.delete(`/categories/${id}/permanent`)
};

export const exchangeRateService = {
  getAll: (params?: any) => api.get('/exchange-rates', { params }),
  getCurrent: () => api.get('/exchange-rates/current'),
  create: (data: any) => api.post('/exchange-rates', data),
  update: (id: number, data: any) => api.put(`/exchange-rates/${id}`, data),
  delete: (id: number) => api.delete(`/exchange-rates/${id}`),
  restore: (id: number) => api.post(`/exchange-rates/${id}/restore`),
  permanentDelete: (id: number) => api.delete(`/exchange-rates/${id}/permanent`),
  getHistory: (params?: any) => api.get('/exchange-rates/history', { params })
};

export const recordService = {
  getAll: (params?: any) => api.get('/records', { params }),
  getCurrentBalance: (params?: any) => api.get('/records/balance', { params }),
  create: (data: any) => api.post('/records', data),
  update: (id: number, data: any) => api.put(`/records/${id}`, data),
  delete: (id: number) => api.delete(`/records/${id}`),
  export: (params?: any) => api.get('/records/export', { params }),
  copyFromPreviousMonth: (accountType: string, copyOption?: 'expense-only' | 'all', fromMonth?: string, toMonth?: string) => api.post('/records/copy-from-previous-month', { accountType, copyOption, fromMonth, toMonth }),
  checkDuplicates: (records: any[]) => api.post('/records/check-duplicates', { records })
};

export const dashboardService = {
  getSummary: (params?: any) => api.get('/dashboard/summary', { params }),
  getAnalytics: (params?: any) => api.get('/dashboard/analytics', { params }),
  getStatistics: (params?: any) => api.get('/dashboard/stats', { params }),
  getMonthlySummary: (params?: any) => api.get('/dashboard/monthly-summary', { params })
};

export const activityLogService = {
  getLogs: (params?: any) => api.get('/audit/logs', { params }),
  getById: (id: number) => api.get(`/audit/logs/${id}`),
  export: (params?: any) => api.get('/audit/logs/export', { params }),
  getUsers: () => api.get('/audit/users'),
  getActionTypes: () => api.get('/audit/activity-types')
};

export const llmService = {
  parseDescription: (description: string, recordType?: string, accountType?: string) => 
    api.post('/llm/parse-description', { description, recordType, accountType }),
  generateReport: (reportType: string, data: any[], parameters?: any) => 
    api.post('/llm/generate-report', { reportType, data, parameters }),
  getStatus: () => api.get('/llm/status')
};

export const cacheService = {
  getStats: () => api.get('/cache/stats'),
  getCachedBalance: (accountType: string) => api.get('/cache/balance', { params: { accountType } }),
  recalculateBalance: (accountType: string, userId?: number) => 
    api.post('/cache/balance/recalculate', { accountType, userId }),
  warmUpCache: () => api.post('/cache/warm-up'),
  getJobQueueStatus: () => api.get('/cache/job-queue/status'),
  invalidateCache: (cacheType: string, pattern: string) => 
    api.post('/cache/invalidate', { cacheType, pattern }),
  cleanExpiredCache: () => api.post('/cache/clean-expired')
};

export const monthlySalaryService = {
  getAll: () => api.get('/monthly-salary'),
  getById: (id: number) => api.get(`/monthly-salary/${id}`),
  create: (data: any) => api.post('/monthly-salary', data),
  update: (id: number, data: any) => api.put(`/monthly-salary/${id}`, data),
  delete: (id: number) => api.delete(`/monthly-salary/${id}`),
  duplicate: (id: number) => api.post(`/monthly-salary/${id}/duplicate`),
  addDetail: (id: number, data: any) => api.post(`/monthly-salary/${id}/details`, data),
  updateDetail: (detailId: number, data: any) => api.put(`/monthly-salary/details/${detailId}`, data),
  deleteDetail: (detailId: number) => api.delete(`/monthly-salary/details/${detailId}`),
  batchUpdateDetails: (id: number, details: any[]) => 
    api.put(`/monthly-salary/${id}/details/batch`, { details }),
  getAvailableEmployees: (id: number) => api.get(`/monthly-salary/${id}/available-employees`),
  createEmployee: (data: any) => api.post('/monthly-salary/employees', data),
  importFromExcel: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/monthly-salary/${id}/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
};

export const backupService = {
  getAll: (params?: any) => api.get('/backups', { params }),
  getById: (id: number) => api.get(`/backups/${id}`),
  create: (data: any) => api.post('/backups', data),
  restore: (id: number, confirmation: string, overrideBackupTable: boolean = false) => 
    api.post(`/backups/${id}/restore`, { confirmation, overrideBackupTable }),
  download: async (id: number) => {
    // Get the file with authentication, then trigger download
    const response = await api.get(`/backups/${id}/download`, {
      responseType: 'blob'
    });
    
    // Create a blob URL and trigger download
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Try to get filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = `backup_${id}.zip`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
};

export default api;
